import React from 'react';

import type { PaymentMethod } from '@stripe/stripe-js';
import _ from 'es-toolkit/compat';

import type { Nullable } from '@clearverify/types';

import {
  StripeGetCustomerPaymentMethodsDocument,
  useStripeDetachPaymentMethodMutation,
  useStripeGetCustomerPaymentMethodsQuery,
  useStripeUpdateCustomerDefaultPaymentMethodMutation,
} from 'src/api';

import { useNotification } from '../app';

export const useStripeCustomerPaymentMethods = (stripeCustomerId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data: paymentMethodsData, loading: loadingPaymentMethods } =
    useStripeGetCustomerPaymentMethodsQuery({
      variables: {
        stripeCustomerId: String(stripeCustomerId),
      },
      skip: !stripeCustomerId,
    });

  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = React.useState<Nullable<string>>();

  React.useEffect(
    () =>
      setDefaultPaymentMethodId(
        paymentMethodsData?.stripeGetCustomerPaymentMethods?.defaultPaymentMethodId,
      ),
    [paymentMethodsData?.stripeGetCustomerPaymentMethods?.defaultPaymentMethodId],
  );

  const paymentMethods: PaymentMethod[] = React.useMemo(
    () =>
      _.concat(
        paymentMethodsData?.stripeGetCustomerPaymentMethods?.cardPaymentMethods || [],
        paymentMethodsData?.stripeGetCustomerPaymentMethods?.achPaymentMethods || [],
      ),
    [paymentMethodsData],
  );

  const [updateDefaultPaymentMethodMutation, { loading: updatingDefaultPaymentMethod }] =
    useStripeUpdateCustomerDefaultPaymentMethodMutation({
      context: createApolloContext({
        errorMessage: 'An error occurred while setting default payment method',
      }),
    });

  const updateDefaultPaymentMethod = React.useCallback(
    async (newDefaultPaymentMethodId: string) => {
      if (!stripeCustomerId) {
        return;
      }

      const prevDefaultPaymentMethodId = defaultPaymentMethodId;

      setDefaultPaymentMethodId(newDefaultPaymentMethodId);

      try {
        await updateDefaultPaymentMethodMutation({
          variables: {
            defaultPaymentMethodId: newDefaultPaymentMethodId,
            customerId: stripeCustomerId,
          },
          refetchQueries: [StripeGetCustomerPaymentMethodsDocument],
        });
      } catch (error) {
        setDefaultPaymentMethodId(prevDefaultPaymentMethodId);
        console.error(error);
      }
    },
    [stripeCustomerId, updateDefaultPaymentMethodMutation, defaultPaymentMethodId],
  );

  const [detachPaymentMethodMutation, { loading: detachingPaymentMethod }] =
    useStripeDetachPaymentMethodMutation();

  const detachPaymentMethod = React.useCallback(
    async (paymentMethodId: string) => {
      await detachPaymentMethodMutation({
        variables: {
          paymentMethodId,
        },
        refetchQueries: [StripeGetCustomerPaymentMethodsDocument],
        awaitRefetchQueries: true,
      });
    },
    [detachPaymentMethodMutation],
  );

  return {
    defaultPaymentMethodId,
    paymentMethods,
    loadingPaymentMethods,
    updateDefaultPaymentMethod,
    updatingDefaultPaymentMethod,
    detachPaymentMethod,
    detachingPaymentMethod,
  };
};
